import React from 'react'
import {
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  Box,
  Typography,
  styled,
  SelectChangeEvent,
} from '@mui/material'
import TablePaginationActions from './TablePaginationActions'

// Styled Select to match TablePagination design
const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}))

// Define types for props
interface RowOption {
  id: string
  value: number
  label: string
}

interface CustomTablePaginationProps {
  rowsPerPageOptions: RowOption[]
  rowsPerPage: number
  count: number
  page: number
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
  onRowsPerPageChange: (event: SelectChangeEvent<unknown>) => void
}

// Main Component
const CustomTablePagination: React.FC<CustomTablePaginationProps> = ({
  rowsPerPageOptions,
  rowsPerPage,
  count,
  page,
  onPageChange,
  onRowsPerPageChange,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: { xs: 0.5, sm: 1 },
        flexWrap: 'nowrap',
        gap: { xs: 0.5, sm: 1 },
      }}
    >
      {/* Rows per Page Label and Dropdown */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            marginRight: 2,
            fontSize: '.75rem',
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Rows per page:
        </Typography>
        <FormControl sx={{ minWidth: { xs: 40, sm: 50 } }}>
          <StyledSelect
            value={rowsPerPage}
            onChange={onRowsPerPageChange}
            displayEmpty
            id='row-per-page-select'
            size='small'
            sx={{
              '& .MuiSelect-select': {
                padding: { xs: '6px 8px', sm: '10px 14px' },
                fontSize: '.75rem',
              },
            }}
          >
            {rowsPerPageOptions.map((option) => (
              <MenuItem key={option.id} value={option.value} id={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      </Box>

      {/* Table Pagination */}
      <TablePagination
        rowsPerPageOptions={[]} // Hide default options
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        ActionsComponent={TablePaginationActions}
        component='div'
        sx={{
          flexShrink: 0,
          '& .MuiTablePagination-spacer': {
            flex: { xs: '0 0 auto', sm: '1 1 auto' },
          },
          '& .MuiTablePagination-displayedRows': {
            margin: { xs: '0 4px 0 0', sm: '0 32px 0 0' },
            fontSize: '.75rem',
          },
        }}
      />
    </Box>
  )
}

export default CustomTablePagination
